import type { APIError, AppState, Dispatch, ThunkAction } from '../../types/app.types';
import type {
  Dosage,
  Drug,
  DrugDetailsResponse,
  Drugs,
  DrugsActions,
  DrugsState,
  Form,
  PriceTiers,
  Quantity,
  SetDosage,
  SetFetching,
  SetForm,
  SetSelectedDrug,
  ToggleCustomQuantity,
} from './drugs.types';
import type { DirectoryResponse, Monograph } from '../../types/api-v0.types';
import { createSelector } from 'reselect';
import { IS_SERVER } from '../../config';
import { SetQuantity } from './drugs.types';

/*************************************************************************
 * Initial Medication State
 **************************************************************************/
export const initialState: DrugsState = {
  isFetching: false,
  formulationError: null,
  customQuantityError: null,
  customQuantityInputIsVisible: false,
  selectedMedSlug: '',
  drugs: {},
  drugsInfo: {},
};

/* Actions */
export function fetchFormulations(slug: string, onlyGet?: boolean): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    dispatch(setFetching());

    // extracted because its used twice in this function
    const _fetchFormulation = () =>
      blinkAPIClient
        .get(`v2/user/drugs/detail/${slug}`)
        .then((res) => res.data)
        .then(
          (data: DrugDetailsResponse) => {
            dispatch({
              type: 'drugs/FETCH_FORMULATIONS_SUCCESS',
              res: data.result.drug,
            });
            return data.result.drug;
          },
          (error: APIError) => {
            dispatch({
              type: 'drugs/FETCH_FORMULATIONS_ERROR',
              res: error,
            });
            return {
              error,
            };
          }
        );

    if (!onlyGet) {
      dispatch({
        type: 'drugs/SET_SELECTED_DRUG',
        slug,
      });
    }

    return _fetchFormulation(); // NOTE: Below is a dead code block I discovered. It was never ran since Sunset release.
    // Keeping it for now so we could refer to it should we discover any regression related to this
    // const drugInCache = getState().drugs.drugs[slug];
    // if (!onlyGet && Boolean(drugInCache)) {
    //   // if the medication is in cache optimistically update the store
    //   dispatch({ type: 'drugs/SET_SELECTED_DRUG', slug });
    //   // we want to get a fresh copy of the formulation
    //   // we need first to update the backend with the user selected value fetched from the cache
    //   // and then fetch the formulation this is a background operation that is user transparent
    //   const { rawQuantity } = getQuantity(drugInCache);
    //   const { medId } = getDosage(drugInCache);
    //   return blinkAPIClient
    //     .put(`v2/user/drugs/detail/${slug}/dosage/${medId}/quantity/${rawQuantity}`)
    //     .then(_fetchFormulation);
    // } else {
    //   dispatch(setFetching());
    //   return _fetchFormulation();
    // }
  };
}

export function fetchDrugs(drugs: Array<string>): ThunkAction<Promise<any>> {
  return function (dispatch): Promise<any> {
    return Promise.all(drugs.map((drug) => dispatch(fetchDrug(drug))));
  };
}

export function fetchDrugDetails(slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`v2/user/drugs/detail/${slug}`)
      .then((res) => res.data)
      .then(
        (data: DrugDetailsResponse) => data.result.drug,
        (err: APIError) => err
      );
  };
}

/* Actions */
export function fetchDrug(slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const drugInCache = getState().drugs.drugs[slug];
    if (drugInCache)
      return Promise.resolve({
        res: drugInCache,
      });
    return blinkAPIClient
      .get(`v2/user/drugs/detail/${slug}`)
      .then((res) => res.data)
      .then((data: DrugDetailsResponse) =>
        dispatch({
          type: 'drugs/FETCH_DRUG_SUCCESS',
          res: data.result.drug,
        })
      );
  };
}

export function fetchDrugInfo(slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const drugsInfoInCache = getState().drugs.drugsInfo[slug];
    if (drugsInfoInCache)
      return Promise.resolve({
        res: drugsInfoInCache,
      });
    return blinkAPIClient
      .get(`directory/${slug}`)
      .then((res) => res.data)
      .then((data: DirectoryResponse) =>
        dispatch({
          type: 'drugs/FETCH_DRUG_INFO_SUCCESS',
          res: data,
        })
      );
  };
}

export function setFetching(): SetFetching {
  return {
    type: 'drugs/SET_FETCHING',
  };
}

export function setFormulation(medId: string, quantity: number): ThunkAction<Promise<any>> {
  return (dispatch, getState): Promise<any> => {
    const drug = getDrug(getState().drugs);

    if (!drug) {
      return null;
    } else {
      // find quanity in formulation
      let foundQuantity = false;

      for (const f of drug.forms) {
        for (const d of f.dosages) {
          if (d.medId === medId) {
            for (const q of d.quantities) {
              if (q.rawQuantity === quantity) foundQuantity = true;
            }
          }
        }
      }

      if (foundQuantity) {
        return Promise.resolve(
          dispatch({
            type: 'drugs/SET_FORMULATION',
            medId,
            quantity,
          })
        );
      } else {
        return dispatch(fetchCustomQuantity(quantity, null, medId));
      }
    }
  };
}

export function setSelectedDrug(slug: string): SetSelectedDrug {
  return {
    type: 'drugs/SET_SELECTED_DRUG',
    slug,
  };
}

export function setForm(formIndex: string): SetForm {
  return {
    type: 'drugs/SET_FORM',
    formIndex,
  };
}

export function setDosage(dosageIndex: string): SetDosage {
  return {
    type: 'drugs/SET_DOSAGE',
    dosageIndex,
  };
}

export function setQuantity(quantityIndex: string): ThunkAction<ToggleCustomQuantity | SetQuantity> {
  return function (dispatch, getState): ToggleCustomQuantity | SetQuantity {
    const drug: Drug | null | undefined = getDrug(getState().drugs);

    if (drug) {
      const numOfQuantities = getDosage(drug).quantities.length;

      if (Number(quantityIndex) >= numOfQuantities) {
        return dispatch({
          type: 'drugs/TOGGLE_CUSTOM_QUANTITY',
        });
      } else {
        return dispatch({
          type: 'drugs/SET_QUANTITY',
          quantityIndex,
        });
      }
    }
  };
}

export function toggleCustomQuantity(): ToggleCustomQuantity {
  return {
    type: 'drugs/TOGGLE_CUSTOM_QUANTITY',
  };
}

export function fetchCustomQuantity(
  quantity: number | string,
  slug: string | null | undefined,
  medId: string | null | undefined,
  getOnly?: boolean
): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    const drug: Drug | null | undefined = getDrug(getState().drugs);
    const q = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    // Backend has a hard limit on quantity
    if (q > 100000 || Number.isNaN(q)) return Promise.resolve();

    if (!drug) {
      // This should never happen, but flow
      return Promise.resolve();
    } else {
      const _slug = slug || getState().drugs.selectedMedSlug;

      const _medId = medId || getDosage(drug).medId;

      const customeDosageIndex = drug.forms[drug.selectedFormIndex].dosages.findIndex(
        (dosage) => dosage.medId === medId
      );
      return blinkAPIClient({
        url: `v2/user/drugs/detail/${_slug}/dosage/${_medId}/quantity/${q}`,
        method: getOnly ? 'get' : 'put',
      })
        .then((res) => res.data)
        .then(
          (data: { result: Quantity }) => {
            if (getOnly) {
              return data.result;
            }

            dispatch({
              type: 'drugs/FETCH_CUSTOM_QUANTITY_SUCCESS',
              res: data.result,
              dosageIndex: customeDosageIndex,
            });
          },
          (err: APIError) => {
            if (getOnly) {
              return err;
            }

            dispatch({
              type: 'drugs/FETCH_CUSTOM_QUANTITY_ERROR',
              res: err,
            });
          }
        );
    }
  };
}

export function setCustomQuantity(quantity: Quantity, dosageIndex: number): any {
  return function (dispatch: Dispatch) {
    dispatch({
      type: 'drugs/SET_CUSTOM_QUANTITY',
      quantity,
      dosageIndex,
    });
  };
}

/*************************************************************************
 * Selectors
 **************************************************************************/
const drugsRoot = (state) => state.drugs;

export const getDrugs: (arg0: AppState) => Record<string, Drug> = createSelector(drugsRoot, (root) => root.drugs);

export function getDrug(state: DrugsState): Drug | null | undefined {
  return state.drugs[state.selectedMedSlug];
}

export function hasDrug(state: Drugs, slug: string): boolean {
  return Boolean(state[slug]);
}

export function getPromotionalCategory(state: DrugsState) {
  const drug: Drug | null | undefined = getDrug(state);
  return drug ? drug.promotionalCategory : null;
}

export function isBranded(state: DrugsState) {
  return Boolean(getPromotionalCategory(state));
}

export function getForm(drug: Drug, index?: number): Form {
  return drug.forms[index !== undefined ? index : drug.selectedFormIndex];
}

export function getDosage(drug: Drug, formIndex?: number, index?: number): Dosage {
  return getForm(drug, formIndex).dosages[index !== undefined ? index : drug.selectedDosageIndex];
}

export function getQuantity(drug: Drug, formIndex?: number, dosageIndex?: number, index?: number): Quantity {
  return getDosage(drug, formIndex, dosageIndex).quantities[index !== undefined ? index : drug.selectedQuantityIndex];
}

export function getDosageMedId(state: DrugsState): string | null | undefined {
  const drug: Drug | null | undefined = getDrug(state);
  return drug ? getDosage(drug).medId : null;
}

export function getPriceTiers(drug: Drug): PriceTiers {
  return getQuantity(drug).price;
}

export type FormulationEditorProps = {
  drug: Drug;
  customQuantityInputIsVisible: boolean;
  formIndex: number;
  dosageIndex: number;
  quantityIndex: number;
  forms: Array<string>;
  dosages: Array<string>;
  quantities: Array<string>;
};

export function getFormulationEditorProps(state: AppState): FormulationEditorProps {
  const drug: Drug | null | undefined = getDrug(state.drugs);

  if (!drug) {
    return {
      drug: null,
      customQuantityInputIsVisible: false,
      formIndex: -1,
      dosageIndex: -1,
      quantityIndex: -1,
      forms: [],
      dosages: [],
      quantities: [],
    };
  } else {
    const forms = drug.forms.map((f) => f.displayForm);
    const dosages = getForm(drug).dosages.map((d) => d.displayDosage);
    const dosage = getDosage(drug);
    const quantities = dosage.quantities.map((q) => q.displayQuantity);
    // The custom quantity option added here can be removed on formulation-editor based on enableCustomQuantities flag
    if (dosage.isCustomQuantityAllowed) quantities.push('Custom Quantity');
    return {
      drug,
      customQuantityInputIsVisible: state.drugs.customQuantityInputIsVisible,
      formIndex: drug.selectedFormIndex,
      dosageIndex: drug.selectedDosageIndex,
      quantityIndex: drug.selectedQuantityIndex,
      forms,
      dosages,
      quantities,
    };
  }
}

/*************************************************************************
 * Helpers
 **************************************************************************/
export function extractDosage(drug: Drug): Dosage {
  const form = drug.forms[drug.selectedFormIndex];
  return form.dosages[drug.selectedDosageIndex];
}

export function extractDosageMedId(drug: Drug): string {
  const dosage: Dosage = extractDosage(drug);
  return dosage.medId;
}

export function extractQuantity(drug: Drug): Quantity {
  return extractDosage(drug).quantities[drug.selectedQuantityIndex];
}

export function extractPriceTiers(drug: Drug): PriceTiers {
  return extractQuantity(drug).price;
}

function extractDosageAndQuantity(drug: Drug): {
  dosage: string;
  quantity: number;
} {
  const { selectedFormIndex: fi, selectedDosageIndex: di, selectedQuantityIndex: qi } = drug;
  const dosage: string = drug.forms[fi].dosages[di].displayDosage;
  const quantity: number = drug.forms[fi].dosages[di].quantities[qi].rawQuantity;
  return {
    dosage,
    quantity,
  };
}

function updateDrugQuantityIndex(_drug: Drug, quantityIndex: string): any {
  const drug = { ..._drug };
  drug.selectedQuantityIndex = Number(quantityIndex);
  return drug;
}

function updateDrugDosageIndex(_drug: Drug, dosageIndex: string): any {
  const drug = { ..._drug };
  const { quantity } = extractDosageAndQuantity(drug);
  // set a new index
  drug.selectedDosageIndex = Number(dosageIndex);
  // search for the same quantity in the new dosage's quantities array
  const foundQuantityIndex = drug.forms[drug.selectedFormIndex].dosages[drug.selectedDosageIndex].quantities.findIndex(
    (q: Quantity) => q.rawQuantity === quantity
  );
  // if we found it set it otherwise set the default for that dosage
  drug.selectedQuantityIndex =
    foundQuantityIndex > -1
      ? foundQuantityIndex
      : drug.forms[drug.selectedFormIndex].dosages[drug.selectedDosageIndex].selectedQuantityIndex;
  return drug;
}

function updateDrugFormIndex(_drug: Drug, formIndex: string): any {
  const drug = { ..._drug };
  // extract dosage and quantity for the previously selected index
  const { dosage, quantity } = extractDosageAndQuantity(drug);
  // set a new index
  drug.selectedFormIndex = Number(formIndex);
  // search for the same dosage in the new form's dosage array
  const foundDosageIndex = drug.forms[drug.selectedFormIndex].dosages.findIndex(
    (d: Dosage) => d.displayDosage === dosage
  );
  // if we found it set it otherwise set the default for that form
  drug.selectedDosageIndex =
    foundDosageIndex > -1 ? foundDosageIndex : drug.forms[drug.selectedFormIndex].selectedDosageIndex;
  // search for the same quantity in the new dosage's quantities array
  const foundQuantityIndex = drug.forms[drug.selectedFormIndex].dosages[drug.selectedDosageIndex].quantities.findIndex(
    (q: Quantity) => q.rawQuantity === quantity
  );
  // if we found it set it otherwise set the default for that dosage
  drug.selectedQuantityIndex =
    foundQuantityIndex > -1
      ? foundQuantityIndex
      : drug.forms[drug.selectedFormIndex].dosages[drug.selectedDosageIndex].selectedQuantityIndex;
  return drug;
}

// TODO:
// we probably want to do all of this data gymanstics at the action level and have this func take indecies
// this will allow support for custom quantities in the query string
// Helper: updateFormulationIndecies(_drug:Drug, formIndex:string, dosageIndex:string, quantityIndex:string):Drug
// Action: updateFormulation(medId: string, quantity: number):ThunkAction
//    -> finds the indecies in the state, dispatch getCustomQuantity if needed then dispatches updateFormulationIndecies
function updateFormulation(_drug: Drug, medId: string, quantity: number): Drug {
  const drug = { ..._drug };
  const found = drug.forms.some((form, fi) =>
    form.dosages.some((dosage, di) => {
      if (dosage.medId === medId) {
        drug.selectedFormIndex = fi;
        drug.selectedDosageIndex = di;
        const quantityIndex = dosage.quantities.findIndex((q) => quantity === q.rawQuantity);
        // if the specified quantity is not found, fallback on the default selectedQuantityIndex for the new dosage or 0.
        drug.selectedQuantityIndex =
          quantityIndex !== -1 ? quantityIndex : drug.forms[fi].dosages[di].selectedQuantityIndex || 0;
        return true;
      } else {
        return false;
      }
    })
  );
  return found ? drug : _drug;
}

function mergeCustomQuantity(_drug: Drug, customQuantity: Quantity, dosageIndex?: number): Drug {
  const drug = { ..._drug };

  const _dosageIndex = dosageIndex && dosageIndex > -1 ? dosageIndex : drug.selectedDosageIndex;

  drug.selectedDosageIndex = _dosageIndex;
  const { quantities } = drug.forms[drug.selectedFormIndex].dosages[_dosageIndex];
  const customQuantityIndex = quantities.findIndex((q) => q.isCustomQuantity);

  if (customQuantityIndex !== -1) {
    drug.selectedQuantityIndex = customQuantityIndex;
    quantities[customQuantityIndex] = customQuantity;
  } else {
    quantities.push(customQuantity);
    drug.selectedQuantityIndex = quantities.length - 1;
  }

  return drug;
}

export type FlatDrug = {
  slug: string;
  medId: string;
  medNameId: string;
  quantityDescription: string;
  priceId: string | null | undefined;
  price: string | null | undefined;
  quantity: number | null | undefined;
  edlpAvailable: boolean;
  bsdAvailable?: boolean;
  hdAvailable: boolean;
  telemedConditions: Array<string>;
  telehAvailable?: boolean;
  insuranceAvailable?: boolean;
  hdRank?: number;
  edlpRank?: number;
  telehRank?: number;
};
export const flattenDrug = (drug: Drug | null | undefined): FlatDrug | null | undefined => {
  if (!drug) return null;
  const form = drug.forms[0];
  let drugDosage;
  let drugQuantity;

  // need to force the default dosage/quantity price returned for telemed homepage
  if (['sildenafil', 'tadalafil'].includes(drug.slug)) {
    const defaultDosage = drug.slug === 'sildenafil' ? '50 mg' : '5 mg';
    const defaultQuantity = drug.slug === 'sildenafil' ? 10 : 30;
    drugDosage = form.dosages.filter((dosage) => dosage.displayDosage === defaultDosage)[0];
    drugQuantity = drugDosage.quantities.filter((qty) => qty.rawQuantity === defaultQuantity)[0];
  } else {
    drugDosage = drug.forms[0].dosages[0];
    drugQuantity = drugDosage.quantities[0];
  }

  const {
    price: { delivery, edlp, local },
  } = drugQuantity;
  return {
    slug: drug.slug,
    medId: drugDosage.medId,
    medNameId: drug.medNameId,
    quantityDescription: drugQuantity.formattedDescription,
    priceId: delivery && delivery.priceId,
    price: delivery && delivery.displayValue,
    quantity: delivery && delivery.quantity,
    edlpAvailable: Boolean(edlp),
    bsdAvailable: Boolean(local),
    hdAvailable: Boolean(delivery),
    telemedConditions: drug.telemedConditions,
  };
};

function parseMonographResponse(response: DirectoryResponse): any {
  if (!response.results || response.results.length === 0) return null;
  const monographs: Record<string, Monograph> = {};
  response.results.forEach((result) => {
    // we actually want missedDose and notes in a `more` attribute :/
    const monograph = { ...result.monograph };

    if (monograph.missedDose || monograph.notes) {
      let content = '';
      if (monograph.missedDose) content += `Missed Doses:\n${monograph.missedDose}\n`;
      if (monograph.notes) content += `Notes:\n${monograph.notes}`;
      monograph.more = content;
    }

    monographs[result.medId] = monograph;
  });
  return monographs;
}

/*************************************************************************
 * Reducer
 **************************************************************************/

export default function drugsReducer(state: DrugsState = { ...initialState }, action: DrugsActions): DrugsState {
  switch (action.type) {
    case 'drugs/SET_FETCHING': {
      return { ...state, isFetching: true };
    }

    case 'drugs/FETCH_DRUG_SUCCESS': {
      const slug = action.res.slug;
      return { ...state, drugs: { ...state.drugs, [slug]: drugReducer(action.res, action) } };
    }

    case 'drugs/FETCH_DRUG_INFO_SUCCESS': {
      const slug = action.res.slug;
      return { ...state, drugsInfo: { ...state.drugsInfo, [slug]: parseMonographResponse(action.res) } };
    }

    case 'drugs/SET_SELECTED_DRUG': {
      return { ...state, selectedMedSlug: action.slug, isFetching: false };
    }

    case 'drugs/FETCH_FORMULATIONS_SUCCESS': {
      const slug = action.res.slug;
      return {
        ...state,
        isFetching: IS_SERVER,
        selectedMedSlug: slug,
        formulationError: null,
        customQuantityInputIsVisible: false,
        customQuantityError: null,
        drugs: { ...state.drugs, [slug]: drugReducer(action.res, action) },
      };
    }

    case 'drugs/FETCH_FORMULATIONS_ERROR': {
      return { ...state, isFetching: false, formulationError: action.res };
    }

    case 'drugs/FETCH_CUSTOM_QUANTITY_ERROR': {
      return { ...state, customQuantityError: action.res.message };
    }

    case 'drugs/SET_FORMULATION':
    case 'drugs/SET_FORM':
    case 'drugs/SET_DOSAGE':
    case 'drugs/SET_QUANTITY':
    case 'drugs/SET_CUSTOM_QUANTITY':
    case 'drugs/FETCH_CUSTOM_QUANTITY_SUCCESS': {
      const drug = getDrug(state);

      if (!drug) {
        return state;
      } else {
        return { ...state, isFetching: false, drugs: { ...state.drugs, [drug.slug]: drugReducer(drug, action) } };
      }
    }

    case 'drugs/TOGGLE_CUSTOM_QUANTITY': {
      return { ...state, customQuantityInputIsVisible: !state.customQuantityInputIsVisible };
    }

    default:
      return state;
  }
}

const drugReducer = (drug: Drug, action: DrugsActions): Drug => {
  switch (action.type) {
    case 'drugs/FETCH_FORMULATIONS_SUCCESS':
      return { ...action.res };

    case 'drugs/SET_FORMULATION':
      return updateFormulation(drug, action.medId, action.quantity);

    case 'drugs/SET_FORM':
      return updateDrugFormIndex(drug, action.formIndex);

    case 'drugs/SET_DOSAGE':
      return updateDrugDosageIndex(drug, action.dosageIndex);

    case 'drugs/SET_QUANTITY':
      return updateDrugQuantityIndex(drug, action.quantityIndex);

    case 'drugs/SET_CUSTOM_QUANTITY':
      return mergeCustomQuantity(drug, action.quantity, action.dosageIndex);

    case 'drugs/FETCH_CUSTOM_QUANTITY_SUCCESS': {
      return mergeCustomQuantity(drug, action.res, action.dosageIndex);
    }

    default:
      return drug;
  }
};
